import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Search } from 'lucide-react';
import { VocabularyWord } from '../components/VocabularyCard';
// Mock Data - All 14 vocabulary words
const DICTIONARY_WORDS: VocabularyWord[] = [{
  id: '1',
  hiragana: 'たかい',
  kanji: '高い',
  romaji: 'takai',
  pitchPattern: [0, 1, 0],
  meaning: 'Expensive / Tall',
  mnemonic: "Imagine a TALL building that costs a lot - it's expensive and high up!",
  exampleSentence: 'このレストランは高いです。',
  exampleTranslation: 'This restaurant is expensive.'
}, {
  id: '2',
  hiragana: 'ねこ',
  kanji: '猫',
  romaji: 'neko',
  pitchPattern: [1, 0],
  meaning: 'Cat',
  mnemonic: 'The cat hurt its NECK-o looking up at the bird.',
  exampleSentence: '猫が好きです。',
  exampleTranslation: 'I like cats.'
}, {
  id: '3',
  hiragana: 'たべる',
  kanji: '食べる',
  romaji: 'taberu',
  pitchPattern: [0, 1, 0],
  meaning: 'To eat',
  mnemonic: 'I want to eat at a TABLE (taberu).',
  exampleSentence: '寿司を食べる。',
  exampleTranslation: 'I eat sushi.'
}, {
  id: '4',
  hiragana: 'みず',
  kanji: '水',
  romaji: 'mizu',
  pitchPattern: [0, 1],
  meaning: 'Water',
  mnemonic: 'Please give MIZU (me some) water.',
  exampleSentence: '水をください。',
  exampleTranslation: 'Water, please.'
}, {
  id: '5',
  hiragana: 'ほん',
  kanji: '本',
  romaji: 'hon',
  pitchPattern: [1, 0],
  meaning: 'Book',
  mnemonic: 'HONestly, I love reading books.',
  exampleSentence: '本を読みます。',
  exampleTranslation: 'I read books.'
}, {
  id: '6',
  hiragana: 'いぬ',
  kanji: '犬',
  romaji: 'inu',
  pitchPattern: [0, 1],
  meaning: 'Dog',
  mnemonic: 'I knew (inu) my dog would be loyal.',
  exampleSentence: '犬が走ります。',
  exampleTranslation: 'The dog runs.'
}, {
  id: '7',
  hiragana: 'あたらしい',
  kanji: '新しい',
  romaji: 'atarashii',
  pitchPattern: [0, 1, 0, 0, 0],
  meaning: 'New',
  mnemonic: 'A TARA SHE (atarashii) bought something new.',
  exampleSentence: '新しい車を買いました。',
  exampleTranslation: 'I bought a new car.'
}, {
  id: '8',
  hiragana: 'おおきい',
  kanji: '大きい',
  romaji: 'ookii',
  pitchPattern: [0, 0, 1, 0],
  meaning: 'Big',
  mnemonic: 'OH KEY (ookii) is so big I can barely hold it.',
  exampleSentence: '大きい家に住んでいます。',
  exampleTranslation: 'I live in a big house.'
}, {
  id: '9',
  hiragana: 'あかい',
  kanji: '赤い',
  romaji: 'akai',
  pitchPattern: [0, 1, 0],
  meaning: 'Red',
  mnemonic: 'A red KAI (akai) pepper is spicy.',
  exampleSentence: '赤いりんごが好きです。',
  exampleTranslation: 'I like red apples.'
}, {
  id: '10',
  hiragana: 'さむい',
  kanji: '寒い',
  romaji: 'samui',
  pitchPattern: [0, 1, 0],
  meaning: 'Cold',
  mnemonic: 'SOME WE (samui) feel cold in winter.',
  exampleSentence: '今日は寒いです。',
  exampleTranslation: 'Today is cold.'
}, {
  id: '11',
  hiragana: 'はやい',
  kanji: '速い',
  romaji: 'hayai',
  pitchPattern: [0, 1, 0],
  meaning: 'Fast',
  mnemonic: 'HAY! (hayai) That car is fast!',
  exampleSentence: '新幹線は速いです。',
  exampleTranslation: 'The bullet train is fast.'
}, {
  id: '12',
  hiragana: 'むずかしい',
  kanji: '難しい',
  romaji: 'muzukashii',
  pitchPattern: [0, 0, 1, 0, 0],
  meaning: 'Difficult',
  mnemonic: 'MUSIC CASH (muzukashii) is difficult to earn.',
  exampleSentence: '日本語は難しいです。',
  exampleTranslation: 'Japanese is difficult.'
}, {
  id: '13',
  hiragana: 'やすい',
  kanji: '安い',
  romaji: 'yasui',
  pitchPattern: [0, 1, 0],
  meaning: 'Cheap',
  mnemonic: 'YES! WE (yasui) found something cheap!',
  exampleSentence: 'この店は安いです。',
  exampleTranslation: 'This store is cheap.'
}, {
  id: '14',
  hiragana: 'おいしい',
  kanji: '美味しい',
  romaji: 'oishii',
  pitchPattern: [0, 1, 0, 0],
  meaning: 'Delicious',
  mnemonic: "OH! EASY (oishii) to eat when it's delicious.",
  exampleSentence: 'このラーメンは美味しいです。',
  exampleTranslation: 'This ramen is delicious.'
}];
interface DictionaryPageProps {
  onExit: () => void;
}
export function DictionaryPage({
  onExit
}: DictionaryPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const filteredWords = DICTIONARY_WORDS.filter(word => word.meaning.toLowerCase().includes(searchQuery.toLowerCase()) || word.hiragana.includes(searchQuery) || word.kanji.includes(searchQuery) || word.romaji.includes(searchQuery.toLowerCase()));
  return <div className="min-h-screen bg-[#FDFBF7] text-gray-800 font-sans flex flex-col" data-id="element-61">
      {/* Header */}
      <header className="px-6 py-6 sticky top-0 bg-[#FDFBF7]/95 backdrop-blur-sm z-10 border-b border-gray-100" data-id="element-62">
        <div className="flex items-center justify-between mb-4" data-id="element-63">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-64">Dictionary</h1>
          <button onClick={onExit} className="p-2 -mr-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors" data-id="element-65">
            <X size={24} data-id="element-66" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative" data-id="element-67">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} data-id="element-68" />
          <input type="text" placeholder="Search words..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white border border-gray-200 rounded-xl py-3 pl-10 pr-4 text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E63946]/20 focus:border-[#E63946] transition-all" data-id="element-69" />
        </div>
      </header>

      {/* Word List */}
      <main className="flex-1 px-6 py-4 overflow-y-auto" data-id="element-70">
        <div className="space-y-3 max-w-md mx-auto pb-8" data-id="element-71">
          {filteredWords.length > 0 ? filteredWords.map((word, index) => <motion.div key={word.id} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.05
        }} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow flex items-center justify-between group cursor-pointer" data-id="element-72">
                <div data-id="element-73">
                  <div className="flex items-baseline gap-2 mb-1" data-id="element-74">
                    <span className="text-lg font-bold text-gray-900" data-id="element-75">
                      {word.kanji}
                    </span>
                    <span className="text-sm text-gray-500 font-medium" data-id="element-76">
                      {word.hiragana}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-77">{word.meaning}</p>
                </div>

                {/* Pitch Accent Mini Viz */}
                <div className="flex items-center gap-0.5 opacity-30 group-hover:opacity-100 transition-opacity" data-id="element-78">
                  {word.pitchPattern.map((pitch, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${pitch === 1 ? 'bg-[#E63946] -mt-1' : 'bg-gray-300 mt-1'}`} data-id="element-79" />)}
                </div>
              </motion.div>) : <div className="text-center py-12 text-gray-400" data-id="element-80">
              <p data-id="element-81">No words found matching "{searchQuery}"</p>
            </div>}
        </div>
      </main>
    </div>;
}